import { useRef } from 'react';

import { useEditFeedFloorPlan } from './useEditFeedFloorPlan';

import type { EditFeedFloorPlanInput } from './useEditFeedFloorPlan';

type FieldValue = string | number | boolean | undefined;
export type FeedFloorPlanFieldValues = Record<string, FieldValue>;
export type FeedFloorPlanSaveResult = { ok: true } | { ok: false; error: unknown; restoredValue?: FieldValue };

/**
 * Serializes whole-floor-plan feed edits. A feed edit rewrites the whole floor plan, so each field change updates
 * the desired values, builds the payload from the latest desired values at send time, and runs requests one at a
 * time - a slower earlier save can't overwrite a newer one. On failure the field rolls back to the last value the
 * server accepted (unless a newer edit already replaced it), so a failed save never re-sends a stale value.
 */
export const useFeedFloorPlanSave = (
  listingAlias: string,
  initialValues: FeedFloorPlanFieldValues,
  buildInput: (values: FeedFloorPlanFieldValues) => EditFeedFloorPlanInput,
) => {
  const { editFeedFloorPlan } = useEditFeedFloorPlan(listingAlias);
  const desiredRef = useRef<FeedFloorPlanFieldValues>({ ...initialValues });
  const acceptedRef = useRef<FeedFloorPlanFieldValues>({ ...initialValues });
  const versionRef = useRef<Record<string, number>>({});
  const chainRef = useRef<Promise<unknown>>(Promise.resolve());

  const saveField = async (field: string, value: FieldValue): Promise<FeedFloorPlanSaveResult> => {
    const version = (versionRef.current[field] ?? 0) + 1;
    versionRef.current[field] = version;
    desiredRef.current = { ...desiredRef.current, [field]: value };

    const chained = chainRef.current
      .catch(() => {})
      .then(() => {
        const sent = { ...desiredRef.current };
        return editFeedFloorPlan(buildInput(sent)).then(() => {
          acceptedRef.current = sent;
        });
      });
    chainRef.current = chained;

    try {
      await chained;
      return { ok: true };
    } catch (error) {
      // Roll back only when no newer edit to this field happened since. Compare the edit version, not the value,
      // because a newer edit can repeat an earlier value (2 -> 3 -> 4 -> 3).
      if (versionRef.current[field] === version) {
        const restoredValue = acceptedRef.current[field];
        desiredRef.current[field] = restoredValue;
        return { ok: false, error, restoredValue };
      }
      return { ok: false, error };
    }
  };

  const waitForPendingSaves = () => chainRef.current.catch(() => {});

  return { saveField, waitForPendingSaves };
};
